import { useMutation, useQuery } from '@tanstack/react-query';
import { getAuthenticated, post, postAuthenticated } from './api';
import {
  GetInTouchRequest,
  PropertyDetailsRequest,
  PropertyDetailsResponseData,
} from '../models/details.models';
import {
  RegridPartialResponse,
  RegridRequest,
  RegridResponse,
} from '../models/regrid.model';
import { PostHomePlacementRequest } from '../models/home-placement.models';

// query must have either id or address property
export const useGetLandDetails = (
  query: PropertyDetailsRequest | undefined | null
) => {
  const url = `land/details`;
  const { isPending, error, data, refetch } =
    useQuery<PropertyDetailsResponseData>({
      queryKey: ['details', query],
      enabled: !!query?.id && !!query?.searchType,
      queryFn: async () => {
        const body = JSON.stringify(query);
        return post<PropertyDetailsResponseData>(url, body);
      },
    });

  return {
    loading: isPending,
    error,
    data: data || undefined,
    refetch,
  };
};

export const useMutateGetInTouch = () => {
  const url = `land/details/get-in-touch`;
  return useMutation({
    mutationKey: ['get-in-touch'],
    mutationFn: async (request: GetInTouchRequest) => {
      const body = JSON.stringify(request);
      return postAuthenticated<GetInTouchRequest>(url, body);
    },
  });
};
export const useMutateHomePlacement = () => {
  const url = `home/placement`;
  return useMutation({
    mutationKey: ['home-on-land'],
    mutationFn: async (request: PostHomePlacementRequest) => {
      const body = JSON.stringify(request);
      return postAuthenticated<PostHomePlacementRequest>(url, body);
    },
  });
};

export const useGetRegridData = (
  query: RegridRequest | undefined,
  isPaidCustomer: boolean | undefined,
  isAdmin: boolean | undefined
) => {
  const url = `land/enhanced/details?parcelnumb=${query?.parcelnumb}&address=${query?.address}&path=${query?.path}`;
  const { isPending, error, data } = useQuery<RegridResponse>({
    queryKey: ['regrid', query],
    enabled: !!query && (isPaidCustomer || isAdmin),
    queryFn: async () => {
      return getAuthenticated<RegridResponse>(url);
    },
    retry: false,
  });
  return {
    loading: isPending,
    error,
    data: data || undefined,
  };
};

export const useGetRegridDataPartial = (query: RegridRequest | undefined) => {
  const url = `land/enhanced/details/partial?parcelnumb=${query?.parcelnumb}&address=${query?.address}&path=${query?.path}`;
  const { isPending, error, data } = useQuery<RegridPartialResponse>({
    queryKey: ['regrid-partial', query],
    enabled: !!query,
    queryFn: async () => {
      return getAuthenticated<RegridPartialResponse>(url);
    },
    retry: false,
  });
  return {
    loading: isPending,
    error,
    data: data || undefined,
  };
};
